import React, { useState, useEffect } from 'react';
import { X, Cookie, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Link } from 'react-router-dom';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    functional: false,
    analytics: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Small delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const handleAcceptAll = () => {
    const allConsent = {
      necessary: true,
      functional: true,
      analytics: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(allConsent));
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    const minimalConsent = {
      necessary: true,
      functional: false,
      analytics: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(minimalConsent));
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    const customConsent = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(customConsent));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 z-[100] backdrop-blur-sm" />
      
      {/* Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[101] p-4 md:p-6">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-[hsl(210,25%,90%)] overflow-hidden">
          {/* Header */}
          <div className="p-6 pb-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[hsl(213,80%,35%,0.1)] flex items-center justify-center flex-shrink-0">
                <Cookie className="w-6 h-6 text-[hsl(213,80%,35%)]" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-[hsl(215,25%,15%)] mb-2">
                  Cookie-Einstellungen
                </h3>
                <p className="text-[hsl(215,15%,45%)] text-sm leading-relaxed">
                  Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. 
                  Sie können wählen, welche Cookies Sie zulassen möchten. Weitere Informationen finden Sie in unserer{' '}
                  <Link to="/datenschutz" className="text-[hsl(213,80%,35%)] hover:underline">
                    Datenschutzerklärung
                  </Link>.
                </p>
              </div>
            </div>
          </div>

          {/* Details Toggle */}
          <div className="px-6">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center text-sm text-[hsl(213,80%,35%)] hover:text-[hsl(213,80%,30%)] font-medium"
            >
              {showDetails ? (
                <>
                  <ChevronUp className="w-4 h-4 mr-1" />
                  Weniger anzeigen
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 mr-1" />
                  Cookie-Einstellungen anpassen
                </>
              )}
            </button>
          </div>

          {/* Cookie Details */}
          {showDetails && (
            <div className="px-6 py-4 space-y-4 border-t border-[hsl(210,25%,92%)] mt-4 bg-[hsl(210,40%,98%)]">
              {/* Necessary Cookies */}
              <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-[hsl(210,25%,90%)]">
                <div className="flex-1 mr-4">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-[hsl(215,25%,15%)]">Notwendige Cookies</h4>
                    <span className="text-xs bg-[hsl(213,80%,35%,0.1)] text-[hsl(213,80%,35%)] px-2 py-0.5 rounded">Erforderlich</span>
                  </div>
                  <p className="text-sm text-[hsl(215,15%,45%)]">
                    Diese Cookies sind für die Grundfunktionen der Website erforderlich (z.B. Session-Verwaltung, Sicherheit).
                  </p>
                </div>
                <Switch checked={true} disabled className="opacity-50" />
              </div>

              {/* Functional Cookies */}
              <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-[hsl(210,25%,90%)]">
                <div className="flex-1 mr-4">
                  <h4 className="font-medium text-[hsl(215,25%,15%)] mb-1">Funktionale Cookies</h4>
                  <p className="text-sm text-[hsl(215,15%,45%)]">
                    Ermöglichen erweiterte Funktionen wie das Speichern Ihrer Präferenzen und Einstellungen.
                  </p>
                </div>
                <Switch 
                  checked={preferences.functional} 
                  onCheckedChange={(checked) => setPreferences({...preferences, functional: checked})}
                />
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-[hsl(210,25%,90%)]">
                <div className="flex-1 mr-4">
                  <h4 className="font-medium text-[hsl(215,25%,15%)] mb-1">Analyse-Cookies</h4>
                  <p className="text-sm text-[hsl(215,15%,45%)]">
                    Helfen uns zu verstehen, wie Besucher unsere Website nutzen, um diese zu verbessern.
                  </p>
                </div>
                <Switch 
                  checked={preferences.analytics} 
                  onCheckedChange={(checked) => setPreferences({...preferences, analytics: checked})}
                />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="p-6 pt-4 flex flex-col sm:flex-row gap-3 border-t border-[hsl(210,25%,92%)] bg-white">
            {showDetails ? (
              <>
                <Button 
                  variant="outline" 
                  onClick={handleAcceptNecessary}
                  className="flex-1 border-[hsl(210,25%,88%)] text-[hsl(215,25%,30%)]"
                >
                  Nur notwendige
                </Button>
                <Button 
                  onClick={handleSavePreferences}
                  className="flex-1 bg-[hsl(213,80%,35%)] hover:bg-[hsl(213,80%,30%)] text-white"
                >
                  Auswahl speichern
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  onClick={handleAcceptNecessary}
                  className="flex-1 border-[hsl(210,25%,88%)] text-[hsl(215,25%,30%)]"
                >
                  Nur notwendige
                </Button>
                <Button 
                  onClick={handleAcceptAll}
                  className="flex-1 bg-[hsl(213,80%,35%)] hover:bg-[hsl(213,80%,30%)] text-white"
                >
                  Alle akzeptieren
                </Button>
              </>
            )}
          </div>

          {/* Footer Note */}
          <div className="px-6 pb-4">
            <p className="text-xs text-[hsl(215,15%,55%)] flex items-center">
              <Shield className="w-3.5 h-3.5 mr-1.5" />
              Ihre Privatsphäre ist uns wichtig. Wir speichern keine personenbezogenen Daten ohne Ihre Zustimmung.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieBanner;
